import React, { useState, useEffect } from 'react';
import { Form, Input, Collapse, Pagination, Button, Row, Col} from 'antd';
import axios from "@common/axios";
import debounce from 'lodash/debounce';
import CircularLoader from "@common/CircularLoader";
import GlossaryTermDialog from "./GlossaryTermDialog";
import { GlossaryTerm, GlossaryHead } from "./GlossaryTerm";
import LetterToolbar from "./LetterToolbar";
import {useScreenMode} from "@common/context/ScreenModeContext";

const { Search } = Input;

const Glossary = props => {
    const {smallScreenMode} = useScreenMode();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(40);
    const [glossaryTerms, setGlossaryTerms] = useState([]);
    const [glossaryTerm, setGlossaryTerm] = useState();
    const [accordionItems, setAccordionItems] = useState([]);
    const [selectedLetter, setSelectedLetter] = useState('A');
    const [glossarySearchTerm, setGlossarySearchTerm] = useState('');
    const [glossarySearchForm] = Form.useForm();
    const [isLoading, setLoading] = useState(true);
    const [glossaryTermId, setGlossaryTermId] = useState();
    const [isGlossaryTermDialogOpen, setGlossaryTermDialogOpen] = useState(false);
    const [letters, setLetters] = useState([]);
    const [error, setError] = useState();

    useEffect(() => {
        if (!selectedLetter) {
            return;
        }
        loadGlossaryTermsByLetter(selectedLetter);
    }, [selectedLetter]);

    useEffect(() => {
        setLetters('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''));
    }, []);

    const loadGlossaryTermsByLetter = (keyLetter) => {
        setLoading(true);
        axios.get('/web/ui/glossary/glossaryTermsByLetter', { params: { keyLetter } })
            .then(response => {
                setGlossaryTerms(response.data);
                setAccordionItems(createAccordionItems(response.data));
                setPage(1);
                setGlossaryTerm(undefined);
                setError(undefined);
            }).catch(error => {
            console.error(error);
            setError('Error loading dictionary terms');
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleSearch = (term) => {
        setGlossarySearchTerm(term);
        if (!term || !term.trim()) {
            loadGlossaryTermsByLetter('A');
            return;
        }
        setLoading(true);
        axios.post(`/web/ui/glossary/searchGlossaryTerms`, { glossaryTerm: term })
            .then(response => {
                setGlossaryTerms(response.data.currPageContents);
                setAccordionItems(createAccordionItems(response.data.currPageContents));
                setPage(1);
                setSelectedLetter('');
                setGlossaryTerm(undefined);
                setError(undefined);
            }).catch(error => {
            console.error(error);
            setError('Error loading dictionary terms');
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleSearchInputChange = debounce((e) => {
        handleSearch(e.target.value);
    }, 800);

    const handlePageChange = (page) => {
        setPage(page);
        setGlossaryTerm(undefined);
    };

    const handleShowSizeChange = (current, size) => {
        setPageSize(size);
        setPage(1);
        setGlossaryTerm(undefined);
    };

    const handleLetterClick = (letter) => {
        setGlossarySearchTerm('');
        setSelectedLetter(letter);
        setGlossaryTerm(undefined);
    };

    const handleGlossaryTermChange = (selectedGlossaryTerm) => {
        setGlossaryTerm(selectedGlossaryTerm);
    };

    const handleLinkClick = (event) => {
        event.preventDefault();
        const glossaryTermId = event.currentTarget.getAttribute('value');
        if (glossaryTermId) {
            setGlossaryTermId(glossaryTermId);
            setGlossaryTermDialogOpen(true);
        }
    };

    const handleCloseGlossaryTermDialog = () => {
        setGlossaryTermDialogOpen(false);
        setGlossaryTermId(null);
    };

    const createAccordionItems = (data) => {
        return data.map((locGlossaryTerm) => {
            return {
                key: locGlossaryTerm.glossaryTermId,
                label: <GlossaryHead glossaryTerm={locGlossaryTerm} />,
                children: <GlossaryTerm glossaryTerm={locGlossaryTerm} showGlossaryLinks={true} onLinkClick={handleLinkClick}/>
            };
        });
    };

    const currentPageData = accordionItems.slice((page - 1) * pageSize, page * pageSize);
    const halfLength = Math.ceil(currentPageData.length / 2);
    const leftColumnData = currentPageData.slice(0, halfLength);
    const rightColumnData = currentPageData.slice(halfLength);

    return (
        <div className="ch-glossary">
            <div>
                <Form
                    form={glossarySearchForm}
                    layout="horizontal"
                    className="ch-form"
                >
                    <Form.Item label="Search" name="glossarySearchTerm" id="glossary-search-label">
                        <Search
                            allowClear
                            onChange={handleSearchInputChange}
                            onSearch={handleSearch}
                            enterButton={
                                <Button
                                    type="primary"
                                    disabled={!glossarySearchTerm || !glossarySearchTerm.trim()}
                                    aria-label="Execute search"
                                >
                                    Search
                                </Button>
                            }
                        />
                    </Form.Item>
                </Form>
            </div>

            <LetterToolbar
                letters={letters}
                selectedLetter={selectedLetter}
                handleLetterClick={handleLetterClick}
            />

            <CircularLoader visible={isLoading} />
            {error && (
                <div className="ch-error-text" role="alert">
                    {error}
                </div>
            )}

            {currentPageData.length === 0 && !isLoading && (
                <div className="ch-warning-text" role="alert">
                    No dictionary terms found for '{selectedLetter + glossarySearchTerm}'
                </div>
            )}

            {currentPageData.length > 0 && !isLoading && (
                <div>
                    <nav aria-label="Pagination Navigation">
                        <Pagination
                            hideOnSinglePage={false}
                            current={page}
                            pageSize={pageSize}
                            total={glossaryTerms.length}
                            align="end"
                            pageSizeOptions={[40, 60, 80]}
                            onChange={handlePageChange}
                            onShowSizeChange={handleShowSizeChange}
                            style={{ textAlign: 'center', marginTop: 20 }}
                        />
                    </nav>

                    {smallScreenMode ? (
                        <Collapse
                            accordion
                            size="small"
                            activeKey={glossaryTerm}
                            onChange={handleGlossaryTermChange}
                            items={currentPageData}
                        />
                    ) : (
                        <Row
                            gutter={[16, 16]}
                            style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}
                        >
                            <Col md={12}>
                                {leftColumnData.length > 0 && (
                                    <Collapse
                                        accordion
                                        size="small"
                                        activeKey={glossaryTerm}
                                        onChange={handleGlossaryTermChange}
                                        items={leftColumnData}
                                    />
                                )}
                            </Col>
                            <Col md={12}>
                                {rightColumnData.length > 0 && (
                                    <Collapse
                                        accordion
                                        size="small"
                                        activeKey={glossaryTerm}
                                        onChange={handleGlossaryTermChange}
                                        items={rightColumnData}
                                    />
                                )}
                            </Col>
                        </Row>
                    )}

                    <nav aria-label="Pagination Navigation">
                        <Pagination
                            hideOnSinglePage={false}
                            current={page}
                            pageSize={pageSize}
                            total={glossaryTerms.length}
                            align="end"
                            pageSizeOptions={[40, 60, 80]}
                            onChange={handlePageChange}
                            onShowSizeChange={handleShowSizeChange}
                            style={{ textAlign: 'center', marginTop: 20 }}
                        />
                    </nav>
                </div>
            )}

            <GlossaryTermDialog
                open={isGlossaryTermDialogOpen}
                glossaryTermId={glossaryTermId}
                onClose={handleCloseGlossaryTermDialog}
            />
        </div>
    );
};

export default Glossary;
